// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo } from 'react';

import type { BendType, BendTypeName, ConduitSize } from '@evolvemep/api-ts';

import { isNil } from 'helpers/isNotNil';
import { useDeepStableValue } from 'hooks/useDeepStableValue';
import type { BendPreviewSpecifications } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreviewGeometry';
import { getBendPreview } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/getBendPreview';
import { useBendCart } from 'modules/Shop/EvolveFabrication/BendCreator/Cart/useBendCart';
import {
  type BendCreatorFormValues,
  useBendCreatorFormContext,
} from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';
import {
  getStandInBendRadius,
  getValidatedBendRadius,
} from 'modules/Shop/EvolveFabrication/BendCreator/State/bendRadius';
import { useBendBuilder } from 'modules/Shop/EvolveFabrication/BendCreator/State/useBendBuilder';
import { useBendTypes } from 'modules/Shop/EvolveFabrication/BendCreator/useBendTypes';
import { useConduits } from 'modules/Shop/EvolveFabrication/useConduits';

/** The bend type the Bend Creator shows before one is picked, so the canvas isn't empty. */
export const STARTING_BEND_TYPE = 'Stub 90' satisfies BendTypeName;
/** The conduit size bends are drawn in until one is chosen. */
const STARTING_CONDUIT_SIZE_NAME = '3"';

type BuildOptions = {
  values: BendCreatorFormValues;
  bendTypes: BendType[] | undefined;
  conduitSizes: ConduitSize[] | undefined;
  /** The bend radius from a validation of these values, in decimal feet, or null to use the stand-in. */
  validatedRadius: number | null;
};

/**
 * Builds preview dimensions from a bend's values, in the chosen conduit size (3" until one is chosen). Until a bend
 * type is picked and its required dimensions are entered, the result is a placeholder: the bend type's default
 * dimensions, or the {@link STARTING_BEND_TYPE}'s before a bend type is picked. Optional dimensions left blank are
 * drawn with the bend type's default values.
 */
const buildBendPreviewSpecifications = ({
  values,
  bendTypes,
  conduitSizes,
  validatedRadius,
}: BuildOptions): BendPreviewSpecifications => {
  const bendType = bendTypes?.find(({ name }) => name === values.bendType);
  const geometry = getBendPreview(values.bendType ?? STARTING_BEND_TYPE);
  const defaults = geometry.getDefaultDimensions();
  geometry.dispose();

  const conduitSize =
    (
      conduitSizes?.find(({ conduitSizeId }) => conduitSizeId === values.conduitSizeId) ??
      conduitSizes?.find(({ name }) => name === STARTING_CONDUIT_SIZE_NAME)
    )?.diameter ?? defaults.conduitSize;
  const built: BendPreviewSpecifications = {
    ...defaults,
    conduitSize,
    bendRadius: validatedRadius ?? getStandInBendRadius(conduitSize),
  };
  if (isNil(bendType) || bendType.inputs.some(({ keyName, isRequired }) => isRequired && isNil(values[keyName]))) {
    return { ...built, isPlaceholder: true };
  }
  bendType.inputs.forEach(({ keyName }) => {
    built[keyName] = values[keyName] ?? defaults[keyName];
  });
  return built;
};

/**
 * Builds the bend preview dimensions from the form's current values, so the model follows the user as they type.
 * The bend radius comes from the last successful validation when it matches the current bend type, conduit size and
 * bender; otherwise a stand-in of 6 × the conduit diameter is used.
 */
export const useBendCreatorPreviewData = (): BendPreviewSpecifications => {
  const { conduitSizes } = useConduits();
  const { bendTypes } = useBendTypes();
  const { lastValidation } = useBendCreatorValidationContext();
  const { values } = useBendCreatorFormContext();

  const specifications = useMemo(
    () =>
      buildBendPreviewSpecifications({
        values,
        bendTypes,
        conduitSizes,
        validatedRadius: getValidatedBendRadius(lastValidation, values),
      }),
    [bendTypes, values, conduitSizes, lastValidation],
  );

  // `values` changes on every keystroke, including fields that don't affect the model, such as the part name.
  // Keeping the result stable stops the preview from rebuilding its geometry for those.
  return useDeepStableValue(specifications);
};

/**
 * The bend the Bend Creator's model shows: a bend previewed from the list, or else whatever the panel holds (the bend
 * being built, or a listed bend being edited, which follows each change).
 */
export const useModelBend = (): {
  bendType: BendTypeName | null;
  bendPreviewData: BendPreviewSpecifications | null;
} => {
  const { conduitSizes } = useConduits();
  const { bendTypes } = useBendTypes();
  const { values } = useBendCreatorFormContext();
  const { bends, previewedBendId } = useBendCart();
  const { editSession } = useBendBuilder();
  const panelPreviewData = useBendCreatorPreviewData();

  const previewedBend = isNil(editSession) ? bends.find(({ cartBendId }) => cartBendId === previewedBendId) : undefined;
  const previewedBendData = useDeepStableValue(
    useMemo(
      () =>
        isNil(previewedBend)
          ? null
          : buildBendPreviewSpecifications({
              values: previewedBend.values,
              bendTypes,
              conduitSizes,
              validatedRadius: previewedBend.validation.response.bendRadius,
            }),
      [previewedBend, bendTypes, conduitSizes],
    ),
  );

  return isNil(previewedBend)
    ? { bendType: values.bendType, bendPreviewData: panelPreviewData }
    : { bendType: previewedBend.values.bendType, bendPreviewData: previewedBendData };
};
